import { type Plugin, ref } from 'vue';
import { createRouter, createWebHistory, type RouteLocationRaw, type Router } from 'vue-router';

import { useSession } from '../services/composables';
import { useBreadcrumbStore, useUrlStore } from '../stores';
import { type CorePluginOptions, defaultOptions } from './corePluginOptions';

export const router = ref<Router | undefined>(undefined);

export const setupRouter: (options?: Partial<CorePluginOptions>) => Plugin = (options?: Partial<CorePluginOptions>) => {
	const { checkSession, clearSession, isAuthenticated, isRefreshable, refreshSession } = useSession();
	const breadcrumbStore = useBreadcrumbStore();
	const urlStore = useUrlStore();

	router.value = createRouter({
		history: createWebHistory(),
		routes: options?.routes ?? defaultOptions.value.routes
	});
	if (!router.value) {
		throw new Error('Router has not been initialized');
	}

	router.value.beforeEach(async (to, from): Promise<RouteLocationRaw | undefined> => {
		checkSession();

		if ((to.name !== from.name) && (to.meta?.query as string[])?.length) {
			if (urlStore.updateQuery(to, from)) {
				return to;
			}
		}
		if (!to.meta?.breadcrumbs) {
			breadcrumbStore.clearBreadcrumbs();
		}
		if (!isRefreshable.value) {
			clearSession();
			if (!to.meta?.public) {
				return {
					name: 'login',
					query: {
						redirect: to.fullPath
					}
				};
			}
			return;
		}
		if (!isAuthenticated.value) {
			if (to.meta?.public) {
				return;
			}
			if (!await refreshSession()) {
				clearSession();
				return {
					name: 'login',
					query: {
						redirect: to.fullPath
					}
				};
			}
			return;
		}
		if (to.name === 'login') {
			return {
				name: 'root'
			};
		}
		return;
	});

	router.value.onError((error, to) => {
		const message = error instanceof Error ? error.message : String(error);
		if (
			message.includes('Failed to fetch dynamically imported module') ||
			message.includes('error loading dynamically imported module') ||
			message.includes('Importing a module script failed')
		) {
			window.location.assign(to.fullPath);
		}
	});

	return router.value;
};